import type { AttendanceCapture } from '../types/faceAttendance';

type BrowserFormData = FormData & {
  append(name: string, value: Blob, filename?: string): void;
};

/**
 * Browsers cannot upload React Native's `{ uri, name, type }` file shape.
 * Copy the captured bytes into a real Blob, and hand each request its own
 * slice so a second upload is not left with an already-read image.
 */
export async function browserAttendanceImage(
  image: Pick<AttendanceCapture, 'uri' | 'fileName' | 'mimeType'>,
): Promise<{ appendTo: (form: FormData) => void }> {
  let response: Response;
  try {
    response = await fetch(image.uri);
  } catch {
    throw Object.assign(
      new Error('Could not read the captured photo. Please try again.'),
      { code: 'CAPTURE_FAILED' },
    );
  }
  if (!response.ok) {
    throw Object.assign(
      new Error('Could not read the captured photo. Please try again.'),
      { code: 'CAPTURE_FAILED' },
    );
  }
  const source = await response.blob();
  const type = source.type || image.mimeType || 'image/jpeg';
  const bytes = await source.arrayBuffer();
  const blob = new Blob([bytes], { type });
  const fileName = image.fileName?.trim() || 'attendance.jpg';
  return {
    appendTo(form) {
      (form as BrowserFormData).append(
        'image',
        blob.slice(0, blob.size, type),
        fileName,
      );
    },
  };
}
